import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ChurchBookAuthResponse } from '../../common/models/church-book/church-book.models';
import { environment } from '../../../environments/environment';

@Injectable({ providedIn: 'root' })
export class ChurchBookAuthService {
  private token: string | null = null;
  private registrationAttempted = false;
  private _tokenPromise: Promise<string> | null = null;
  private readonly base = environment.churchBook.apiUrl;

  constructor(private http: HttpClient) {}

  ensureToken(): Promise<string> {
    if (this.token) return Promise.resolve(this.token);
    if (!this._tokenPromise) {
      this._tokenPromise = this._authenticate().catch(err => {
        this._tokenPromise = null;
        throw err;
      });
    }
    return this._tokenPromise;
  }

  invalidate(): Promise<void> {
    this.token = null;
    this._tokenPromise = null;
    return Promise.resolve();
  }

  private async _authenticate(): Promise<string> {
    const cfg = environment.churchBook;
    if (!cfg.apiUrl) {
      throw new Error('ChurchBook: churchBook.apiUrl is not configured.');
    }
    if (cfg.apiUrl.includes('PLACEHOLDER')) {
      console.warn('ChurchBook: churchBook.apiUrl contains a PLACEHOLDER value — update environment.ts before use.');
    }
    if (!this.registrationAttempted) {
      await this.register();
      this.registrationAttempted = true;
    }
    await this.login();
    if (!this.token) {
      throw new Error('ChurchBook: login succeeded but no token was returned.');
    }
    return this.token;
  }

  private register(): Promise<void> {
    return lastValueFrom(
      this.http.post(`${this.base}/auth/register`, {
        email: environment.churchBook.email,
        password: environment.churchBook.password,
      }).pipe(
        catchError(err => err.status === 409 ? of(null) : throwError(() => err))
      )
    ).then(() => {});
  }

  private login(): Promise<void> {
    return lastValueFrom(
      this.http.post<ChurchBookAuthResponse>(`${this.base}/auth/login`, {
        email: environment.churchBook.email,
        password: environment.churchBook.password,
      })
    ).then(res => { this.token = res.token; });
  }
}
